import React, {useEffect, useState} from 'react';
import {Form} from 'react-final-form';
import axios from 'axios';
import {
    TextInput
} from 'react-admin';
import {RESOURCES as resources} from '../resources';
import {getPostHeaders} from '../utils/headers';
import FieldDescription from './FieldDescription';
import FieldDivider from './FieldDivider';
import {useDialogStyles, useLoadingStyles} from '../global/dialogStyles';
import {viewStyles} from '../global/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';

/**
 * A Material UI Dialog to display a Title Create form
 *
 * @param openDialog
 * @param onCloseFunc
 * @param onCompleteFunc
 * @param selectedOrgId
 * @param success
 * @returns {JSX.Element}
 * @constructor
 */
const TitleCreateDialog = (
    {
        openDialog,
        onCloseFunc,
        onCompleteFunc,
        selectedOrgId,
        success
    }) => {

    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formMessage, setFormMessage] = useState({
        error: false,
        message: null
    });

    // Effect: Open dialog from parent
    useEffect(() => {
        return setOpen(openDialog);
    }, [openDialog]);

    const validateForm = (values) => {
        return !(
            !values.new_title_name
        );
    };

    const handleSubmit = (values) => {
        const formValues = {
            name: values?.new_title_name ?? '',
            org_id: (selectedOrgId) ? selectedOrgId : ''
        };
        let errorMessage = 'Sorry, there was an error creating this Title.';
        const resource = resources.Show.CREATE(formValues);
        setSubmitting(true);
        axios({
            method: resource.method,
            url: resource.uri,
            data: formValues,
            headers: getPostHeaders(true)
        }).then((response) => {
            if (response.data && response.data.id) {
                setSubmitting(false);

                // Complete creation
                if (typeof onCompleteFunc === 'function') {
                    onCompleteFunc(response.data);
                }
                if (!response.data) {
                    setFormMessage({
                        error: true,
                        message: errorMessage
                    });
                } else {
                    setFormMessage({
                        error: false,
                        message: 'Title created.'
                    });
                }
                setTimeout(() => {
                    handleClose(null);
                }, 1000);
            } else {
                setSubmitting(false);
                setFormMessage({
                    error: true,
                    message: errorMessage
                });
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
            } else if (error.request) {
                // No response
            }
            setSubmitting(false);
            setFormMessage({
                error: true,
                message: errorMessage
            });
        });
    };

    const handleClose = () => {
        setOpen(false);
        if (typeof onCloseFunc === 'function') {
            onCloseFunc();
        }
    };

    const classes = useDialogStyles();
    const loadingClasses = useLoadingStyles();

    return (
        <>
            <Dialog
                fullWidth
                maxWidth={false}
                onClose={handleClose}
                aria-labelledby="Product-Create"
                open={open}
                className={classes.root}
                transitionDuration={{
                    enter: 100,
                    exit: 150
                }}
            >
                <MuiDialogTitle disableTypography className={classes.title}>
                    <Typography variant="h6">
                        Create Title
                    </Typography>
                    <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
                        <CloseIcon/>
                    </IconButton>
                </MuiDialogTitle>
                <MuiDialogContent dividers>
                    <Form
                        onSubmit={handleSubmit}
                        mutators={{
                            setValue: ([field, value], state, {changeValue}) => {
                                changeValue(state, field, () => value)
                            }
                        }}
                        render={({form, handleSubmit, values}) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <FieldDescription
                                        instructions={true} marginBottom={true}
                                        text='Add a Title by adding a Name.'
                                    />
                                    <TextInput label="Name of Title"
                                               source="new_title_name"
                                               multiline fullWidth={true}/>
                                    <FieldDivider type="break" margin="bottom"/>

                                    <FieldDescription
                                        marginBottom={true}
                                        description={true}
                                        text="After clicking Create Title, this Title should be available to select."
                                    />
                                    <hr style={viewStyles.break}/>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        color="primary"
                                        style={viewStyles.buttonInline}
                                        disabled={(validateForm(values) === false || submitting || success)}
                                        onClick={() => handleSubmit()}
                                    >
                                        Create Title
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        style={viewStyles.buttonInline}
                                        disabled={(submitting || success)}
                                        onClick={() => handleClose()}
                                    >
                                        Cancel
                                    </Button>
                                    {submitting &&
                                        <>
                                            <CircularProgress size={30} className={loadingClasses.root}/>
                                            <p className={loadingClasses.text}>Creating Title...</p>
                                        </>
                                    }
                                    {formMessage.message &&
                                        <p className={(formMessage.error) ? loadingClasses.textError : loadingClasses.text}>
                                            {formMessage.message}
                                        </p>
                                    }
                                </form>
                            )
                        }}
                    >
                    </Form>
                </MuiDialogContent>
            </Dialog>
        </>
    )
};

export default TitleCreateDialog;
